/**
 * How strongly a shop's section colour shows (location dashboard bands and
 * section tiles), one spec for web and the app.
 *
 * The colour only ever paints accents: the band's tint, its rule and left
 * edge, and the icon tile. Names and counts stay on theme text colours. At
 * the old 12% tint every colour sank into the dark ground and blue, violet,
 * teal and sage read alike (Alex, 2026-10-25), so dark mode tints harder; a
 * light ground needs less for the same difference.
 *
 * No imports, so React Native can deep-import it
 * (`@turn-connect/ui/src/theme/sectionColor`).
 */
export const sectionColorSpec = Object.freeze({
  /** Share of the colour in the band's ground, by theme. */
  bandTint: Object.freeze({ dark: 0.3, light: 0.16 }),
  /** Share of the colour in the band's bottom rule. */
  rule: 0.55,
  /** Share of the colour behind an icon on its tile (ringed in the full colour). */
  tileTint: 0.4,
});

/** A 0–1 share as the two hex digits appended to `#rrggbb` ("4D" for 0.3). */
export function alphaHex(share: number): string {
  return Math.round(Math.min(1, Math.max(0, share)) * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();
}

/**
 * `top` at `share` over `ground`, as one solid #rrggbb (what color-mix in
 * srgb gives on web): a tinted badge that a section colour behind it can't
 * show through. Both colours as #rrggbb.
 */
export function mixHex(top: string, share: number, ground: string): string {
  const ch = (hex: string, i: number) => parseInt(hex.replace('#', '').slice(i, i + 2), 16);
  return `#${[0, 2, 4]
    .map((i) => Math.round(ch(top, i) * share + ch(ground, i) * (1 - share)))
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`;
}
